import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import apis from '@src/apis';
import wavingHand from '@src/assets/images/waving-hand.png';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import actions from '@src/redux/actions';
import { STATUS_SURVEY, SURVEY_TYPE } from '@src/constants/survey';
import {
  buildSurveyPayload,
  isFormComplete,
  parseSurveyQuestions,
  selectAnswer,
} from '@src/services/educatorsSurvey';
import Question from './Question';
import { StyledSurveyPopup } from './index.style';

// Shared Typography variants carry -0.4px tracking, which this popup does not
// use. Overriding per usage keeps the rest of the app untouched.
const NO_LETTER_SPACING = { letterSpacing: 0 };

// The survey carries no placeholder of its own, so each question that shows a
// dropdown names the wording it needs here.
const SELECT_PLACEHOLDER_KEYS = {
  expertise: 'educatorsSurveySelectExpertise',
  educationLevel: 'educatorsSurveySelectEducationLevel',
};

const BUTTON_BASE_STYLE = {
  height: '40px',
  padding: '8px 16px',
  borderRadius: '8px',
  '& .MuiTypography-root': NO_LETTER_SPACING,
};

const SKIP_BUTTON_STYLE = {
  ...BUTTON_BASE_STYLE,
  backgroundColor: COLOR.white,
  border: `1px solid ${COLOR.indigo[16]}`,
};

const SUBMIT_BUTTON_STYLE = {
  ...BUTTON_BASE_STYLE,
  border: `1px solid ${COLOR.yellow.default}`,
  '&:disabled': {
    opacity: 1,
    backgroundColor: COLOR.yellow[32],
    borderColor: 'transparent',
    cursor: 'not-allowed',
  },
  '&.Mui-disabled .MuiTypography-root': { color: COLOR.indigo[32] },
};

const SurveyPopup = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [otherTexts, setOtherTexts] = useState({});
  const openedAt = useRef(null);
  const isClosing = useRef(false);

  useEffect(() => {
    if (!open) return;

    const fetchQuestions = async () => {
      const response = await apis.survey.getSurveys(SURVEY_TYPE.EDUCATOR);
      setQuestions(parseSurveyQuestions(response?.result));
    };

    setAnswers({});
    setOtherTexts({});
    openedAt.current = moment();
    isClosing.current = false;
    fetchQuestions();
  }, [open]);

  const handleSelect = (question) => (option) => {
    const nextAnswers = selectAnswer({ answers, question, option });
    const selectedOptions = nextAnswers[question.key]?.selectedOptions || [];

    setAnswers(nextAnswers);

    // Dropping the custom option should drop what was typed for it too, so a
    // stale note never reaches the payload.
    if (
      !selectedOptions.some((item) => item.isCustom) &&
      otherTexts[question.key]
    ) {
      setOtherTexts({ ...otherTexts, [question.key]: '' });
    }
  };

  const handleChangeOtherText = (question) => (text) =>
    setOtherTexts({ ...otherTexts, [question.key]: text });

  const saveSurvey = (status) => {
    const duration = moment().diff(openedAt.current, 'milliseconds');

    apis.survey
      .updateSurvey(
        {
          status,
          steps: buildSurveyPayload({ answers, otherTexts, duration }),
        },
        SURVEY_TYPE.EDUCATOR,
      )
      .then((response) => {
        if (response?.status) return;

        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'educatorsSurveySaveError',
          }),
        );
      });
  };

  // The click closes the popup and the request finishes on its own — waiting on
  // it would leave the survey sitting there for a round trip after the user has
  // said they are done with it. A failure can only be reported, since by then
  // there is nothing left on screen to try again from.
  //
  // The guard is a ref rather than state: a second click can arrive before a
  // state update has rendered, and each one that got through would send the
  // answers again.
  const closeWithStatus = (status) => {
    if (isClosing.current) return;
    isClosing.current = true;

    saveSurvey(status);
    onClose(status);
  };

  const handleSkip = () => closeWithStatus(STATUS_SURVEY.SKIPPED);

  const handleSubmit = () => closeWithStatus(STATUS_SURVEY.DONE);

  // Esc dismisses the survey the same way skipping does; a backdrop click must
  // not, so a half-filled survey survives a stray click outside.
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') return;
    handleSkip();
  };

  return (
    <StyledSurveyPopup open={open} onClose={handleDialogClose}>
      <div className="survey-header">
        <div className="survey-title-row">
          <img className="survey-icon" src={wavingHand} alt="" />
          <Typography
            variant="body-24-bold"
            color={COLOR.darkSlate}
            className="survey-title"
            style={NO_LETTER_SPACING}
          >
            {t('educatorsSurveyTitle')}
          </Typography>
        </div>
        <Typography
          variant="body-14-regular"
          color={COLOR.graySlate}
          className="survey-subtitle"
        >
          {t('educatorsSurveySubtitle')}
        </Typography>
      </div>

      <div className="survey-forms">
        {questions.map((question) => (
          <Question
            key={question.key}
            question={question}
            answer={answers[question.key]}
            otherText={otherTexts[question.key]}
            placeholder={t(
              SELECT_PLACEHOLDER_KEYS[question.key] ||
                'educatorsSurveySelectOption',
            )}
            onSelect={handleSelect(question)}
            onChangeOtherText={handleChangeOtherText(question)}
          />
        ))}
      </div>

      <div className="survey-footer">
        <CommonButton
          variant="outlined"
          customStyle={SKIP_BUTTON_STYLE}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.indigo.default,
          }}
          onClick={handleSkip}
        >
          {t('skip')}
        </CommonButton>
        <CommonButton
          variant="contained"
          disabled={!isFormComplete(questions, answers, otherTexts)}
          bgrDisabled={COLOR.yellow[32]}
          customStyle={SUBMIT_BUTTON_STYLE}
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.indigo.default,
          }}
          onClick={handleSubmit}
        >
          {t('educatorsSurveySubmit')}
        </CommonButton>
      </div>
    </StyledSurveyPopup>
  );
};

export default SurveyPopup;
